import { useState } from "react";
import { Video, PenTool, Code, BookOpen, TrendingUp, Shield, Palette, Check } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SparkleButton from "../components/SparkleButton";
import { services } from "../mock";
import { Link } from "react-router-dom";

const iconMap = {
  Video,
  PenTool,
  Code,
  BookOpen,
  TrendingUp,
  Shield,
  Palette
};

const ServicesPage = () => {
  const [hoveredService, setHoveredService] = useState(null);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-block animate-fade-in">
              <Badge className="px-4 py-2 bg-gradient-to-r from-yellow-400/20 to-orange-500/20 border border-yellow-400/40 text-yellow-300 text-sm font-bold">
                ⚡ Our Services ⚡
              </Badge>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold animate-slide-in-left">
              Professional Freelance <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-400 bg-clip-text text-transparent">Services</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed animate-slide-in-right">
              From creative design to technical development - I personally handle every project with expertise and dedication.
            </p>
            <div className="golden-accent-line"></div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon] || Code;
              return (
                <Card
                  key={service.id}
                  className="dark-card border border-yellow-500/20 hover:border-yellow-400 animate-scale-in group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setHoveredService(service.id)}
                  onMouseLeave={() => setHoveredService(null)}
                >
                  <CardContent className="p-8 space-y-6">
                    <div className="flex items-start justify-between">
                      <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-yellow-500/50">
                        <Icon className="text-black" size={32} />
                      </div>
                      <span className="text-4xl font-bold text-gray-700">0{service.id}</span>
                    </div>

                    <div className="space-y-3">
                      <h3 className="text-2xl font-bold text-white group-hover:text-yellow-400 transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-gray-300 leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    <div className="space-y-2 pt-4 border-t border-gray-700">
                      <h4 className="font-bold text-sm text-yellow-400 uppercase tracking-wide">
                        Key Features
                      </h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center space-x-2 text-sm text-gray-300">
                            <Check className="text-green-400 flex-shrink-0" size={16} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={`transition-all duration-300 ${hoveredService === service.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                      <Button 
                        asChild
                        className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold mt-4 shadow-lg shadow-yellow-500/50"
                      >
                        <Link to="/contact">
                          Get Started →
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-black border-y border-yellow-500/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <span className="text-yellow-400 font-bold text-xs uppercase tracking-[0.2em]">🔄 My Process 🔄</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              How I <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Work</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              A proven personal workflow that delivers exceptional results
            </p>
            <div className="golden-accent-line"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery", description: "Understanding your goals, challenges, and vision" },
              { step: "02", title: "Strategy", description: "Crafting a tailored plan to achieve your objectives" },
              { step: "03", title: "Execution", description: "Bringing ideas to life with precision and creativity" },
              { step: "04", title: "Delivery", description: "Ensuring excellence in every detail before launch" }
            ].map((process, index) => (
              <div
                key={index}
                className="relative animate-fade-in group"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="text-center space-y-4">
                  <div className="relative inline-block">
                    <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto shadow-xl shadow-yellow-500/50 group-hover:scale-110 transition-transform">
                      <span className="text-3xl font-bold text-black">{process.step}</span>
                    </div>
                    {index < 3 && (
                      <div className="hidden lg:block absolute top-1/2 left-full w-full h-0.5 bg-gradient-to-r from-yellow-400 to-transparent"></div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors">{process.title}</h3>
                  <p className="text-gray-300 text-sm">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white border-y border-yellow-500/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold">
              Ready to <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Get Started?</span>
            </h2>
            <p className="text-xl text-gray-300">
              Let's discuss how my services can help transform your business
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <SparkleButton onClick={() => window.location.href = '/contact'}>
                Contact Us 💬
              </SparkleButton>
              <SparkleButton variant="blue" onClick={() => window.location.href = '/portfolio'}>
                View Our Work 🎨
              </SparkleButton>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;